import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import PropTypes from "prop-types";

// components:
import Tooltip from "core/Tooltip";
import TruncateText from "core/TruncateText";

// icons:
import Add from "core/ReusableIcons/Add";
import Checkmark from "core/ReusableIcons/Checkmark";
import Private from "core/ReusableIcons/Private";

// styles:
import colors from "core/colors";
import breakpoints from "core/breakpoints";
import { KitButton } from "./styles";

// custom hooks:
import { useViewport } from "js/hooks/useViewport";

//mutations:
import { SAVE_TO_KIT, REMOVE_FROM_KIT } from "./mutations";
import { GET_KITS_DATA } from "./queries";

const ChooseKit = ({ iconId, kits, kitIdsWithIcon }) => {
    const { browserWidth } = useViewport();
    const isMobile = browserWidth < breakpoints.tablet.px;
    const privateIconText = "Only viewable by you";

    return (
        <>
            {kits.map(kit => {
                const [isProcessingRequest, updateProcessingRequest] = useState(
                    false
                );
                const isInKit = kitIdsWithIcon.includes(kit.id);
                const isPrivate = !kit.isPublic;

                const mutationType = isInKit ? REMOVE_FROM_KIT : SAVE_TO_KIT;
                const [updateKit] = useMutation(mutationType, {
                    refetchQueries: [
                        {
                            query: GET_KITS_DATA,
                            variables: {
                                iconId
                            }
                        }
                    ]
                });

                const handleUpdateKit = e => {
                    e.preventDefault();

                    updateProcessingRequest(true);

                    updateKit({
                        variables: {
                            iconId,
                            kitId: kit.id
                        }
                    });

                    setTimeout(() => updateProcessingRequest(false), 450); // setTimeout to avoid the button jumping around too fast between states
                };

                const buttonStyleType = isInKit ? "solid" : "outline";
                const buttonJustifyContent =
                    !isProcessingRequest && isPrivate
                        ? "space-between"
                        : "flex-start";
                const buttonIcon = isInKit ? <Checkmark /> : <Add />;
                const privateIconColor = isInKit
                    ? colors.primary.diamond
                    : colors.secondary.orca;

                return kit.isLocked ? null : (
                    <KitButton
                        key={kit.id}
                        styleType={buttonStyleType}
                        justifyContent={buttonJustifyContent}
                        onClick={handleUpdateKit}
                        isProcessing={isProcessingRequest}
                        isDisabled={isProcessingRequest}
                        width="100%"
                        iconPosition="left"
                        processingIconPosition="left"
                    >
                        <div>
                            {buttonIcon}
                            <TruncateText charLimit={isMobile ? 32 : 58}>
                                {kit.name}
                            </TruncateText>
                        </div>
                        {isPrivate && (
                            <Tooltip size="small" message={privateIconText}>
                                <Private
                                    fillColor={privateIconColor}
                                    title={privateIconText}
                                />
                            </Tooltip>
                        )}
                    </KitButton>
                );
            })}
        </>
    );
};

ChooseKit.propTypes = {
    iconId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    kits: PropTypes.array
};

export default ChooseKit;
